import { ConnectedAccountResponseDto } from '@helios/frontend-api-v2-client';
import { TakeoverProps } from '@palmetto/dsm-components';
import { useEffect, useState } from 'react';
import { useConnectedAccounts } from '../../../../api/connected-accounts/useConnectedAccounts';
import { FlowStepProps, useTranslation } from '@helios/frontend-core';

import UtilityConnectionCompletedView from './UtilityConnectionCompletedView';

const POLLING_INTERVAL = 2000;
const MAX_POLLING_DURATION = 60000;

export enum ArcadiaIframeConnectionStatus {
  PENDING = 'PENDING',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
}

export enum ConnectionScreens {
  IN_PROGRESS = 'in-progress',
  CONNECTED = 'connected',
  CHECK_AGAIN_LATER = 'check-again-later',
  EMAIL_APPROVAL_NEEDED = 'email-approval-needed',
  RETRY_2FA = 'retry-2fa',
  USERNAME_OR_PASSWORD_INCORRECT = 'incorrect-credentials',
  CONNECTION_FAILED = 'connection-failed',
}

const arcadiaIframeStatusMap: Record<ArcadiaIframeConnectionStatus, ConnectionScreens> = {
  [ArcadiaIframeConnectionStatus.PENDING]: ConnectionScreens.IN_PROGRESS,
  [ArcadiaIframeConnectionStatus.LOGIN_SUCCESS]: ConnectionScreens.CONNECTED,
};

const displayCancelButton: ConnectionScreens[] = [
  ConnectionScreens.IN_PROGRESS,
  ConnectionScreens.CONNECTION_FAILED,
  ConnectionScreens.USERNAME_OR_PASSWORD_INCORRECT,
  ConnectionScreens.RETRY_2FA,
];

export interface Props extends FlowStepProps {
  connectionStatus: ArcadiaIframeConnectionStatus;
  propertyId: string;
  setHeaderDetails: (details: Partial<TakeoverProps>) => void;
}

export function UtilityConnectionCompleted({
  nextStep,
  previousStep,
  setStep,
  totalSteps,
  connectionStatus,
  propertyId,
  setHeaderDetails,
}: Props) {
  const [isPolling, setIsPolling] = useState(connectionStatus === ArcadiaIframeConnectionStatus.PENDING);
  const [displayScreen, setDisplayScreen] = useState<ConnectionScreens>(arcadiaIframeStatusMap[connectionStatus]);
  const { t } = useTranslation();
  const { data: connectedAccounts } = useConnectedAccounts({
    params: {
      providerName: 'arcadia',
      propertyId: propertyId,
    },
    refetchInterval: isPolling ? POLLING_INTERVAL : undefined,
  });

  useEffect(() => {
    if (connectedAccounts?.length && connectedAccounts.length > 0 && connectedAccounts[0].status === 'connected') {
      const screen = mapConnectedAccountToScreen(connectedAccounts[0]);
      if (screen !== ConnectionScreens.IN_PROGRESS) {
        setIsPolling(false);
      }
      setDisplayScreen(screen);
    }
  }, [connectedAccounts]);

  useEffect(() => {
    if (isPolling) {
      const timeoutId = setTimeout(() => {
        setIsPolling(false);
        setDisplayScreen(ConnectionScreens.CHECK_AGAIN_LATER);
      }, MAX_POLLING_DURATION);

      return () => clearTimeout(timeoutId);
    }
  }, [isPolling]);

  const mapConnectedAccountToScreen = (connectedAccount: ConnectedAccountResponseDto): ConnectionScreens => {
    if (connectedAccount.status === 'connected' || connectedAccount.status === 'receivingData') {
      return ConnectionScreens.CONNECTED;
    }
    if (connectedAccount.status === 'inProgress' || connectedAccount.status === 'error') {
      const credentialResource = connectedAccount.resources?.credential;
      const accountResource = connectedAccount.resources?.account;

      const providerStatusDetail = credentialResource?.statusDetail ?? accountResource?.statusDetail;

      if (providerStatusDetail) {
        switch (providerStatusDetail) {
          case 'UNSUCCESSFUL_EMAIL_APPROVAL':
            return ConnectionScreens.EMAIL_APPROVAL_NEEDED;
          case 'MULTI_FACTOR_AUTHENTICATION_FAILURE':
          case 'MULTI_FACTOR_AUTHENTICATION_REFRESH_REQUIRED':
            return ConnectionScreens.RETRY_2FA;
          case 'INVALID_CREDENTIALS':
            return ConnectionScreens.USERNAME_OR_PASSWORD_INCORRECT;
          case 'OTHER_ACCESS_LIMITATION':
          case 'USER_REVOKED_ACCESS':
            return ConnectionScreens.CONNECTION_FAILED;
        }
      }

      if (connectedAccount.status === 'error') {
        return ConnectionScreens.CONNECTION_FAILED;
      }
    }

    return ConnectionScreens.IN_PROGRESS;
  };

  useEffect(() => {
    if (displayCancelButton.includes(displayScreen)) {
      setHeaderDetails({
        heading: t(`connectUtilityTakeover.takeover-heading.connect-utility`),
        topNavProps: {
          leftButton: {
            onClick: nextStep,
            text: t('common.cancel'),
          },
        },
      });
    } else {
      setHeaderDetails({
        heading: t(`connectUtilityTakeover.takeover-heading.connect-utility`),
      });
    }
  }, [displayScreen]);

  return (
    <UtilityConnectionCompletedView
      displayScreen={displayScreen}
      nextStep={nextStep}
      previousStep={previousStep}
      setStep={setStep}
      totalSteps={totalSteps}
    />
  );
}

export default UtilityConnectionCompleted;
